/**
 * Remaining-actions title keys used by ApplicationInfo instruction blocks.
 */
export function getRemainingTitle(remaining) {
  switch (remaining) {
    case 0:
      return "NO_ACTION_REMAINING";
    case 1:
      return "ONE_ACTION_REMAINING";
    case 2:
      return "TWO_ACTIONS_REMAINING";
    default:
      return "THREE_ACTIONS_REMAINING";
  }
}

/**
 * Count used for the instruction *title* when a card docs-validation row is shown
 * after docs / optional video ID / signature rows. Matches legacy
 * `docsUploadedSignaturePendingInstructions`, `docsUploadedAndSignedInstructions`,
 * and `docsPendingAndSignedInstructions` title behavior.
 */
export function getTitleRemainingWithOptionalCardValidation({
  docsDone,
  isKycVideoRelevant,
  kycDone,
  signatureDone,
  includeCardValidation,
}) {
  const primaryRemaining =
    (docsDone ? 0 : 1) +
    (isKycVideoRelevant ? (kycDone ? 0 : 1) : 0) +
    (signatureDone ? 0 : 1);

  if (!includeCardValidation) {
    return primaryRemaining;
  }

  const allPrimaryDone =
    docsDone && (!isKycVideoRelevant || kycDone) && signatureDone;

  if (allPrimaryDone) {
    return 1;
  }

  return primaryRemaining + 1;
}

/**
 * @typedef {{ mainTextId: string, additionalTextId: string|null, iconKey: 'done'|'undone' }} RemainingActionListItemSpec
 */

/**
 * @param {object} params
 * @param {boolean} params.docsDone
 * @param {boolean} params.isKycVideoRelevant
 * @param {boolean} params.kycDone
 * @param {boolean} params.signatureDone
 * @param {string|undefined} params.titleDynamicDataTexts
 * @param {boolean} params.includeCardValidation
 * @param {boolean} [params.docsOnly] When true, only the documentation row (analysing / upload-docs message).
 * @returns {{ title: string, titleDynamicDataTexts?: string, listItems: RemainingActionListItemSpec[] }}
 */
export function buildRemainingActionsSpec({
  docsDone,
  isKycVideoRelevant,
  kycDone,
  signatureDone,
  titleDynamicDataTexts,
  includeCardValidation,
  docsOnly = false,
}) {
  if (docsOnly) {
    const listItems = [
      {
        mainTextId: docsDone ? "DOCS_COMPLETED" : "DOCS_PENDING",
        additionalTextId: docsDone ? null : "DOCS_PENDING_INFO",
        iconKey: docsDone ? "done" : "undone",
      },
    ];
    return {
      title: "ONE_ACTION_REMAINING",
      ...(titleDynamicDataTexts != null ? { titleDynamicDataTexts } : {}),
      listItems,
    };
  }

  const titleRemaining = getTitleRemainingWithOptionalCardValidation({
    docsDone,
    isKycVideoRelevant,
    kycDone,
    signatureDone,
    includeCardValidation,
  });

  const listItems = [
    {
      mainTextId: docsDone ? "DOCS_COMPLETED" : "DOCS_PENDING",
      additionalTextId: docsDone ? null : "DOCS_PENDING_INFO",
      iconKey: docsDone ? "done" : "undone",
    },
    ...(isKycVideoRelevant
      ? [
          {
            mainTextId: "VIDEO_ID",
            additionalTextId: kycDone ? null : "VIDEO_ID_PENDING_INFO",
            iconKey: kycDone ? "done" : "undone",
          },
        ]
      : []),
    {
      mainTextId: signatureDone ? "SIGNATURE_COMPLETED" : "SIGNATURE_PENDING",
      additionalTextId: signatureDone ? null : "SIGNATURE_PENDING_INFO",
      iconKey: signatureDone ? "done" : "undone",
    },
    ...(includeCardValidation
      ? [
          {
            mainTextId: "DOCS_VALIDATION_PENDING",
            additionalTextId: "DOCS_VALIDATION_PENDING_INFO",
            iconKey: "undone",
          },
        ]
      : []),
  ];

  return {
    title: getRemainingTitle(titleRemaining),
    ...(titleDynamicDataTexts != null ? { titleDynamicDataTexts } : {}),
    listItems,
  };
}
